import React, { SetStateAction, useEffect, useState } from 'react';

import type {
  TokenExchangeDTO
} from '../../services/apiClient';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import GoogleLoginButton from '../../components/auth/GoogleLoginButton';
import Button from '../../components/common/Button';
import FormField from '../../components/common/FormField';
import SimpleAlert from '../../components/common/SimpleAlert';
import AuthLayout from '../../components/layout/AuthLayout';
import { ALLOW_ONLY_ADMIN_LOGIN, GOOGLE_CLIENT_ID } from '../../config/config';
import { getFirstAvailableRoute } from '../../utils/routePermissions';
import { getPermissions, getToken } from '../../utils/tokenUtils';
import { useLogin } from '../../hooks/useLogin';

const Login: React.FC = () => {
  const [emailOrPhone, setEmailOrPhone] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [twoFactorCode, setTwoFactorCode] = useState<string>('');
  const [showTwoFactorInput, setShowTwoFactorInput] = useState<boolean>(false);
  const [tempAccessToken, setTempAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: Location })?.from;
  const redirectPath = from ? `${from.pathname}${from.search ?? ''}` : undefined;

  const { handleLogin, handleTwoFactorVerification, handleGoogleSignIn, loading: loginLoading, errorMessage, setErrorMessage } = useLogin({
    setTempAccessToken,
    setShowTwoFactorInput,
    redirectPath,
  });


  useEffect(() => {
    const token = getToken(); // Now checks sessionStorage first, then localStorage
    if (token) {
      if (redirectPath) {
        navigate(redirectPath);
        return;
      }
      const userPermissions = getPermissions() || [];
      const landingPage = getFirstAvailableRoute(userPermissions);
      navigate(landingPage);
    }
  }, [navigate, redirectPath]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    handleLogin(emailOrPhone, password);
  };

  const handleTwoFactorVerificationSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    handleTwoFactorVerification(emailOrPhone, tempAccessToken, twoFactorCode);
  };

  const handleGoogleSignInSubmit = async (token: TokenExchangeDTO): Promise<void> => {
    handleGoogleSignIn(token);
  };

  return (
    <AuthLayout title="Login" description="Sign in to access your account.">
      <div className="mt-8">
        {ALLOW_ONLY_ADMIN_LOGIN && (
          <SimpleAlert
            variant="info"
            message={
              <div>
                <p className="font-semibold mb-2">System Temporarily Unavailable</p>
                <p className="mb-2">
                  The Natshoot system is currently offline while we resolve identified technical issues following our recent launch. We apologise for the inconvenience and appreciate your patience as we work to improve the platform.
                </p>
                <p className="mb-2">
                  The system will be back online on <strong>Monday, 2026/04/13</strong>.
                </p>
                <p>
                  For support please send an email to{' '}
                  <a 
                    href="mailto:member@natshoot.co.za" 
                    className="underline font-semibold"
                  >
                    member@natshoot.co.za
                  </a>
                </p>
              </div>
            }
          />
        )}
        
        {errorMessage && (
          <div className="mb-4 text-sm text-red-600">{errorMessage}</div>
        )}

        {!showTwoFactorInput ? (
          <form onSubmit={handleSubmit}>
            <FormField
              id="emailOrPhone"
              label="Email or Phone"
              type="text"
              value={emailOrPhone}
              onChange={(e) => setEmailOrPhone(e.target.value)}
              placeholder="email@example.com or phone number"
            />
            <div className="mt-6">
              <FormField
                id="password"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your Password"
              />
            </div>
            <div className="mt-6">
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Loading...' : 'Sign In'}
              </Button>
            </div>
            <div className="mt-6">
              <span>or</span>
            </div>
            <div className="mt-6">
              {/*Google*/}
              <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <GoogleLoginButton
                  handleGoogleSignIn={handleGoogleSignInSubmit}
                  setErrorMessage={setErrorMessage}
                />
              </GoogleOAuthProvider>
            </div>
          </form>
        ) : (
          <form onSubmit={handleTwoFactorVerificationSubmit}>
            <div className="text-left">
              <p className="text-left text-gray-700 dark:text-gray-300 mb-4">
                Enter the 2FA code from your authentication app.
              </p>
              <FormField
                id="twoFactorCode"
                label=""
                type="text"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                maxLength={6}
                placeholder="6-digit code"
              />
            </div>
            <div className="mt-6">
              <Button type="submit" className="w-full" disabled={loginLoading}>
                {loginLoading ? 'Verifying...' : 'Verify'}
              </Button>
            </div>
          </form>
        )}

        <p className="mt-6 text-sm text-center text-gray-400">
          Forgot your password?{' '}
          <Link
            to="/forgot-password"
            className="text-black focus:outline-none focus:underline hover:underline"
          >
            Reset it now
          </Link>
        </p>
        <p className="text-sm text-center text-gray-400">
          Need an account?{' '}
          <Link
            to="/join"
            className="text-black focus:outline-none focus:underline hover:underline"
          >
            Join here
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default Login;
